// CardAnalysis — ქონების შეფასება, the FIRST tab of the deal editor (owner 2026-09-12, migration 1065).
//
// The DATABASE computes everything shown here (card_analysis_v1): the statistics groups and the
// normal price, the traits with their coverage, the most-alike active flats, and the questions for
// the call. This component draws that JSON and sends the agent's answers back — it reasons about
// nothing, and NO MODEL is involved anywhere on this path (owner: "this is fully deterministic, no
// Orpheus is needed here; Orpheus looks for gaps and what is really special, the numbers are ours").
//
// Four rules the drawing keeps, all owner rulings:
//   · a percentile is "N % of peers ask less", never "N % cheaper" (2026-09-11, read backwards once);
//   · a trait's rarity is measured among peers WHO HAVE AN ANSWER, with the coverage beside it —
//     not mentioning is not observing an absence, so the box says what share we simply do not know;
//   · every comparable is restated at THIS flat's condition, building class and floor position (1063);
//   · the most-alike list carries links, because the agent shows those adverts to the owner on the
//     call (owner 2026-09-12: "they will use that examples to negotiate price with data and examples").
import { parseMoneyInput } from '../../lib/moneyInput.js'
import { useEffect, useRef, useState } from 'react'
import { ManualPropertyModal } from './ManualPropertyModal.jsx'
import { MarketReportPanel } from './MarketReportPanel.jsx'
import { latinGeorgianSuggestion } from '../../lib/latinGeorgian.js'
import { yearBuiltValid, YEAR_BUILT_MIN, yearBuiltMax } from '../../lib/buildingFacts.js'
import { t, tLog } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { getCardAnalysis, answerCardQuestion, getValuationAdjustments, addValuationAdjustment, getMarketStats } from '../../db/core.js'
import { listingDeepLink } from '../../lib/deskUrl.js'
import { ListingMap } from './ListingMap.jsx'

const fmt = n => (n == null ? '—' : Math.round(Number(n)).toLocaleString('en-US'))
// A price range in a small tile: 128k–176k fits where 128,000–176,000 is cut off.
const fmtK = n => (n == null ? '—' : Number(n) >= 1e6 ? `${(Number(n) / 1e6).toFixed(1)}M` : Number(n) >= 1e4 ? `${Math.round(Number(n) / 1000)}k` : fmt(n))
const sub = (key, vars) => Object.entries(vars).reduce((s, [k, v]) => s.split(`{${k}}`).join(String(v)), t(key))

// Every section is expandable and open by default (owner: "expandable"): an agent on a call scans
// the conclusion, then opens the one group they are being argued with about.
function Section({ title, hint, open, onToggle, children, right = null }) {
  return (
    <section className="rounded-xl border border-border">
      <button type="button" onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left">
        <span className="text-sm font-semibold">{title}</span>
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          {right}
          <span className={open ? 'inline-block rotate-180 transition-transform' : 'inline-block transition-transform'}>
            <Icon name="chevron-down" />
          </span>
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3">
          {hint && <p className="text-[11px] text-muted-foreground mb-2">{hint}</p>}
          {children}
        </div>
      )}
    </section>
  )
}

function Subject({ s, cov }) {
  const bits = [
    s.area_sqm ? `${s.area_sqm} მ²` : null,
    s.rooms ? `${s.rooms} ოთ.` : null,
    s.floor != null ? `სართ. ${s.floor}${s.floors_total ? `/${s.floors_total}` : ''}` : null,
    s.reno, s.bclass, s.series,
    s.has_lift === true ? 'ლიფტი' : s.has_lift === false ? 'ლიფტის გარეშე' : null,
    s.metro_m != null ? sub('desk_val_metro', { m: s.metro_m, name: s.metro_name || '' }) : null,
  ].filter(Boolean)
  return (
    <div className="rounded-xl bg-muted/40 px-3 py-2 text-sm">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-semibold tabular-nums">${fmt(s.price_usd)} · ${fmt(s.ppm_usd)}/მ²</span>
        <span className="text-xs text-muted-foreground">{s.intent}</span>
      </div>
      <div className="text-xs text-muted-foreground mt-0.5">{bits.join(' · ')}</div>
      {/* The honest x % (owner: "we state this is special from what we know x %, we have no info
          about 100−x %"). The axes are the ones the price actually turns on. */}
      {cov && (
        <div className="text-[11px] mt-1 tabular-nums">
          {sub('desk_val_coverage', { k: cov.known_axes, n: cov.total_axes, p: cov.known_pct })}
          {cov.unknown?.length
            ? <span className="text-muted-foreground"> · {t('desk_val_unknown')}: {cov.unknown.join(', ')}</span>
            : null}
        </div>
      )}
    </div>
  )
}

function Stats({ st }) {
  return (
    <>
      {/* The final say, in one sentence: the normal price for THIS condition and floor position,
          and where the ask sits against it. */}
      {st.conclusion_ka && (
        <p className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm mb-2">{st.conclusion_ka}</p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs tabular-nums">
          <thead className="text-muted-foreground">
            <tr className="text-left">
              <th className="py-1 pr-2 font-medium">{t('desk_val_col_group')}</th>
              <th className="py-1 pr-2 font-medium text-right">{t('desk_val_col_n')}</th>
              <th className="py-1 pr-2 font-medium text-right">{t('desk_val_col_median')}</th>
              <th className="py-1 pr-2 font-medium text-right">{t('desk_val_col_iqr')}</th>
              <th className="py-1 pr-2 font-medium text-right">{t('desk_val_col_pct')}</th>
              <th className="py-1 font-medium text-right">{t('desk_val_col_dev')}</th>
            </tr>
          </thead>
          <tbody>
            {(st.groups || []).map(g => (
              <tr key={g.ord} className={`border-t border-border/40 ${g.clears_bar ? '' : 'text-muted-foreground/60'}`}
                title={g.clears_bar ? '' : t('desk_val_too_few')}>
                <td className="py-1 pr-2">{g.label_ka}</td>
                <td className="py-1 pr-2 text-right">{g.n}</td>
                <td className="py-1 pr-2 text-right">${fmt(g.median_ppm)}</td>
                <td className="py-1 pr-2 text-right">{fmt(g.p25_ppm)}–{fmt(g.p75_ppm)}</td>
                <td className="py-1 pr-2 text-right">{g.percentile != null ? `${g.percentile}%` : '—'}</td>
                <td className={`py-1 text-right ${g.deviation_pct > 3 ? 'text-amber-600 dark:text-amber-400' : g.deviation_pct < -3 ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
                  {g.deviation_pct != null ? `${g.deviation_pct > 0 ? '+' : ''}${g.deviation_pct}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// THE PRICE BREAKDOWN (owner 2026-09-17, core 1316–1319: "the end game is a user filling their apartment
// details and getting a calculated median price — on each component"). The database computes it
// (valuation_breakdown_v2); this draws it. Base = the median of the closest comparable flats with a trusted
// price, restated to renovated / middle floor; then this flat's condition and floor steps, each with the
// index level it came from, its evidence count and confidence. An unknown input is never guessed — it is
// listed and the range is wider.
const pctTxt = p => (p == null ? '—' : `${p > 0 ? '+' : ''}${p}%`)
export function Breakdown({ bd, subject }) {
  if (!bd) return null
  if (bd.error) return <p className="text-xs text-muted-foreground">{t('desk_val_bd_none')}</p>
  const b = bd.base || {}
  const e = bd.estimate || {}
  const steps = [
    ['condition', bd.steps?.condition],
    ['floor', bd.steps?.floor],
  ].filter(([, s]) => s)
  const level = l => t(`desk_val_bd_level_${l}`)
  const ask = subject?.ppm_usd
  const askVsEstimate = ask && e.ppm ? Math.round((ask / e.ppm - 1) * 100) : null
  return (
    <div className="space-y-1.5 text-xs" data-val-breakdown>
      <div className="flex items-baseline justify-between gap-2">
        <span>
          {sub('desk_val_bd_base', { level: level(b.level), n: b.comparables ?? 0 })}
          <span className="text-muted-foreground"> · {t('desk_val_bd_reference')}</span>
        </span>
        <span className="tabular-nums font-medium">${fmt(b.median_ppm)}/მ²</span>
      </div>
      {steps.map(([k, s]) => (
        <div key={k} className="flex items-baseline justify-between gap-2 border-t border-border/40 pt-1.5">
          <span className="min-w-0">
            <span>{t(`desk_val_bd_${k}`)}: {t(`desk_val_bd_cls_${s.class}`)}</span>
            <span className="block text-[11px] text-muted-foreground">
              {sub('desk_val_bd_step_meta', { level: level(s.level), n: s.n ?? 0, c: s.confidence ?? '—' })}
            </span>
          </span>
          <span className={`shrink-0 tabular-nums font-medium ${s.pct < 0 ? 'text-amber-600 dark:text-amber-400' : s.pct > 0 ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
            {pctTxt(s.pct)}
          </span>
        </div>
      ))}
      <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-semibold">{t('desk_val_bd_estimate')}</span>
          <span className="tabular-nums font-semibold">${fmt(e.price)} · ${fmt(e.ppm)}/მ²</span>
        </div>
        <div className="flex items-baseline justify-between gap-2 text-[11px] text-muted-foreground tabular-nums">
          <span>{t('desk_val_bd_range')}: ${fmt(e.low_price)}–${fmt(e.high_price)}</span>
          {askVsEstimate != null && <span>{t('desk_val_bd_ask')}: ${fmt(ask)}/მ² ({pctTxt(askVsEstimate)})</span>}
        </div>
      </div>
      {bd.unknowns?.length ? (
        <p className="text-[11px] text-muted-foreground">
          {t('desk_val_bd_unknown')}: {bd.unknowns.map(u => (u === 'condition' || u === 'floor') ? t(`desk_val_bd_unknown_${u}`) : u).join(', ')}
        </p>
      ) : null}
    </div>
  )
}

// The bar is read as: filled = peers KNOWN to have it, hollow = peers known NOT to have it, and the
// gap to 100 % = peers nobody has answered for. Three states, never two.
function Traits({ tr, floorPct }) {
  const top = tr.top || []
  return (
    <>
      {top.length === 0 && <p className="text-xs text-muted-foreground">—</p>}
      <ul className="space-y-1.5">
        {top.map(x => (
          <li key={x.feature_key} className="text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{x.name_ka}</span>
              <span className="tabular-nums text-muted-foreground">{sub('desk_val_trait_line', { r: x.rarity_pct, c: x.coverage_pct })}</span>
            </div>
            <div className="mt-0.5 h-1.5 w-full rounded bg-muted overflow-hidden flex">
              <span className="h-full bg-primary/70" style={{ width: `${Math.max(1, (x.rarity_pct || 0) * ((x.coverage_pct || 0) / 100))}%` }} />
              <span className="h-full bg-muted-foreground/25" style={{ width: `${Math.max(0, 100 - (x.rarity_pct || 0)) * ((x.coverage_pct || 0) / 100)}%` }} />
            </div>
          </li>
        ))}
      </ul>
      {tr.defines_the_cohort?.length ? (
        <p className="text-[11px] text-muted-foreground mt-2">{t('desk_val_trait_common')}: {tr.defines_the_cohort.join(', ')}</p>
      ) : null}
      {tr.not_comparable_yet?.length ? (
        <details className="mt-1 text-[11px] text-muted-foreground">
          <summary className="cursor-pointer">{sub('desk_val_trait_uncovered', { p: floorPct })} · {tr.not_comparable_yet.length}</summary>
          <p className="mt-1">{tr.not_comparable_yet.map(x => `${x.name_ka} (${x.with_n}/${x.known_n}, ${x.coverage_pct}%)`).join(' · ')}</p>
        </details>
      ) : null}
    </>
  )
}

// THE ADDRESS QUESTION (1066, owner 2026-09-12: "add select pin on map or write in address as an
// option. if they dont agree two addresses represented"). Both readings are shown as they stand —
// the stored address and what the advert text says — and neither is pre-selected, because an
// ambiguous address is settled by the agent on the call, never silently by us (ruling 2026-09-11).
// Three ways to answer, all in one box: choose one of the two, type the street and number, or drop
// a pin on the map. The map is the same ListingMap the modal uses, in pick mode.
function AddressQuestion({ q, busy, onAnswer }) {
  const rows = q.addresses || []
  const stored = rows.find(r => r.source === 'stored') || null
  // ⚠️ ONE CONTROL PER QUESTIONABLE LAYER, AND NOTHING FOR THE REST (owner 2026-09-12: "we need
  // to ask whats missing whats ambiguous / we look for district street number building sub
  // district everything about address whats questionable we ask" — and, minutes earlier, "when
  // advert says street name why we ask it again"). `parts` comes from the analysis (1093/1094):
  // six layers, each `known`, `contested`, `missing` or `not_applicable`. A known layer is printed
  // as a fact; a missing one with a closed list (the raion's own უბნები) is a row of chips; a
  // missing free one is one input; a contested one shows both readings and the agent picks. A box
  // in front of an answer we already hold reads as being asked twice, which is what was reported.
  const parts = Array.isArray(q.parts) ? q.parts : []
  const part = k => parts.find(x => x.part === k) || null
  const open = parts.filter(x => x.state === 'missing' || x.state === 'contested')
  const known = parts.filter(x => x.state === 'known')
  // With no `parts` (an older analysis) the 1092 behaviour is the fallback — never a street box
  // over a street we already know.
  const legacyAsks = q.asks || (stored?.street_ka ? 'number' : 'street_and_number')
  const isOpen = (k, legacy) => parts.length ? !!open.find(x => x.part === k) : legacy
  const askStreet = isOpen('street', legacyAsks !== 'number')
  const askHouse = isOpen('house_number', true)
  const askSub = isOpen('subdistrict', false)
  const askKorpus = isOpen('korpus', false)
  const [street, setStreet] = useState('')
  const [house, setHouse] = useState('')
  const [korpus, setKorpus] = useState('')
  const [pin, setPin] = useState(null)
  const [mapOpen, setMapOpen] = useState(false)
  const lat = pin?.lat ?? stored?.lat ?? null
  const lng = pin?.lng ?? stored?.lng ?? null
  const LABEL = {
    city: t('desk_val_addr_city'), district: t('desk_val_addr_district'), subdistrict: t('desk_val_addr_sub'),
    street: t('desk_val_addr_street'), house_number: t('desk_val_addr_house'), korpus: t('desk_val_addr_korpus'),
  }
  const Btn = ({ children, onClick, tone = '' }) => (
    <button type="button" disabled={busy} onClick={onClick}
      className={`rounded-lg border border-border px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-50 ${tone}`}>{children}</button>
  )
  const subPart = part('subdistrict')
  const districtPart = part('district')
  const streetPart = part('street')
  // ONE DIRECT QUESTION for the first open part (owner 2026-10-09: "questions must be very clear direct … not to
  // repeat what we already know"): two readings → which one; a contested district → which of the two; no street →
  // which street (in the district we know); a street → its number; then the block; then the neighbourhood.
  const pairOf = p => (p?.candidates || []).slice(0, 2).map(c => c.name_ka || c.value_ka || c.code)
  const heading = rows.length > 1 ? t('desk_val_addr_q_which')
    : districtPart?.state === 'contested' && pairOf(districtPart).length === 2
      ? tLog('desk_val_addr_q_district', { a: pairOf(districtPart)[0], b: pairOf(districtPart)[1] })
    : askStreet ? ((subPart?.value_ka || districtPart?.value_ka)
        ? tLog('desk_val_addr_q_street_in', { district: subPart?.value_ka || districtPart?.value_ka }) : t('desk_val_addr_q_street'))
    : askHouse ? tLog('desk_val_addr_q_number', { street: streetPart?.value_ka || stored?.street_ka || t('desk_val_addr_street') })
    : askKorpus ? t('desk_val_addr_q_korpus')
    : askSub ? t('desk_val_addr_q_sub')
    : q.question_ka
  // street candidates (a contested street) are taps, never typing; "other" stays the street field below
  const streetChoices = streetPart?.state === 'contested' ? (streetPart.candidates || []) : []
  return (
    <div className="mt-1 space-y-2">
      <div className="text-sm font-medium" data-val-addr-q>{heading}</div>
      {streetChoices.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {streetChoices.map((c, i) => (
            <Btn key={c.street_id || c.code || i} onClick={() => onAnswer({ kind: 'address', street_name: c.name_ka || c.value_ka || null })}>
              {c.name_ka || c.value_ka || c.code}
            </Btn>
          ))}
        </div>
      )}
      {known.length > 0 && (
        <div className="text-[11px] text-muted-foreground">
          {known.map(k => `${LABEL[k.part] || k.part}: ${k.value_ka || '—'}`).join(' · ')}
        </div>
      )}
      {rows.length > 1 && (
        <ul className="space-y-1">
          {rows.map((r, i) => (
            <li key={i} className="flex items-start justify-between gap-2 rounded-lg border border-border px-2 py-1">
              <span className="min-w-0 text-xs">
                <span className="text-muted-foreground">{r.source === 'stored' ? t('desk_val_addr_stored') : t('desk_val_addr_text')}: </span>
                <span className="font-medium">{r.label_ka || '—'}</span>
                {r.house_number ? <span className="text-muted-foreground"> · {r.house_number}</span> : null}
              </span>
              <Btn onClick={() => onAnswer({
                kind: 'address',
                street_name: r.street_ka || null, house_number: r.house_number || null,
                free_text: r.street_ka ? null : (r.label_ka || null),
                note: `${t('desk_val_addr_chose')}: ${r.source === 'stored' ? t('desk_val_addr_stored') : t('desk_val_addr_text')} — ${r.label_ka || ''}`,
              })}>{t('desk_val_addr_this_one')}</Btn>
            </li>
          ))}
        </ul>
      )}
      {/* The sub-district is a CLOSED list — the raion's own children — so it is chips, never a
          text field (owner ruling 2026-09-10: fill the same vocabularies a human agent gets). */}
      {askSub && (subPart?.candidates || []).length > 0 && (
        <div>
          <div className="text-[11px] text-muted-foreground">{subPart.why_ka || LABEL.subdistrict}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            {subPart.candidates.map(c => (
              <Btn key={c.code} onClick={() => onAnswer({ kind: 'address', district_code: c.code })}>{c.name_ka}</Btn>
            ))}
          </div>
        </div>
      )}
      {/* A contested raion: both sides stand, and the answer is recorded rather than applied (1093). */}
      {districtPart?.state === 'contested' && (districtPart.candidates || []).length > 0 && (
        <div>
          <div className="text-[11px] text-muted-foreground">{districtPart.why_ka}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            {districtPart.candidates.map(c => (
              <Btn key={c.code} onClick={() => onAnswer({ kind: 'address', district_code: c.code })}>
                {c.name_ka}{c.basis ? ` · ${c.basis === 'pin' ? t('desk_val_addr_pin_basis') : t('desk_val_addr_stored')}` : ''}
              </Btn>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center gap-1.5 flex-wrap">
        {askStreet && (
          <input value={street} onChange={e => setStreet(e.target.value)} placeholder={t('desk_val_addr_street')}
            className="w-44 rounded-lg border border-border bg-background px-2 py-0.5 text-xs" />
        )}
        {askHouse && (
          <input value={house} onChange={e => setHouse(e.target.value)} placeholder={t('desk_val_addr_house')}
            className="w-20 rounded-lg border border-border bg-background px-2 py-0.5 text-xs" />
        )}
        {askKorpus && (
          <input value={korpus} onChange={e => setKorpus(e.target.value)} placeholder={t('desk_val_addr_korpus')}
            className="w-20 rounded-lg border border-border bg-background px-2 py-0.5 text-xs" />
        )}
        {(askStreet || askHouse || askKorpus) && (
          <Btn onClick={() => (street.trim() || house.trim() || korpus.trim()) && onAnswer({
            kind: 'address',
            street_name: street.trim() || (askStreet ? null : stored?.street_ka || null),
            house_number: house.trim() || null, korpus: korpus.trim() || null,
          })}>{t('desk_val_save')}</Btn>
        )}
        {lat != null && lng != null && (
          <Btn onClick={() => setMapOpen(o => !o)}>{mapOpen ? t('desk_val_addr_map_hide') : t('desk_val_addr_map')}</Btn>
        )}
        {/* "the owner does not know" (owner 2026-10-09): records that the agent asked, as a comment on topic
            'address:unknown'; core 3138 then rests the question for 30 days. Never evidence about the address. */}
        <Btn tone="text-muted-foreground" onClick={() => onAnswer({ kind: 'comment', feature_key: 'address:unknown', note: t('desk_val_addr_unknown') })}>
          {t('desk_val_addr_unknown')}
        </Btn>
      </div>
      {mapOpen && lat != null && lng != null && (
        <div className="space-y-1">
          <div className="h-56 w-full overflow-hidden rounded-lg border border-border">
            <ListingMap lat={lat} lng={lng} precise={!!pin} onPick={setPin} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground tabular-nums">
              {pin ? `${pin.lat}, ${pin.lng}` : t('desk_val_addr_map_hint')}
            </span>
            {pin && <Btn onClick={() => onAnswer({ kind: 'address', lat: pin.lat, lng: pin.lng })}>{t('desk_val_addr_pin_save')}</Btn>}
          </div>
        </div>
      )}
    </div>
  )
}

// The comment (owner 2026-09-12: "agent may just write comment if they dont know what to select").
// It asserts nothing and nothing is promoted from it — it goes to the reading lane as a note, and
// the ones already left are shown so the same thing is not written twice.
function CommentBox({ topic, comments, busy, onAnswer, label = null, className = 'mt-1' }) {
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)
  const list = comments || []
  // „gaiyida ukve", „ar tanamshromlobs" (2026-09-16: an agent's comments in Latin letters, which
  // Georgian search and the reading lane cannot match). OFFERED, never applied: the spelling is
  // ambiguous (k/q, t/T), so the agent sees the Georgian and presses to take it.
  const suggestion = latinGeorgianSuggestion(text)
  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setOpen(o => !o)}
          className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <Icon name="message-circle" /> {label || t('desk_val_comment')}{list.length ? ` · ${list.length}` : ''}
        </button>
      </div>
      {list.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {list.map((c, i) => <li key={i} className="text-[11px] text-muted-foreground">„{c.body}"</li>)}
        </ul>
      )}
      {open && (
        <div className="mt-1 flex items-center gap-1.5">
          <input value={text} onChange={e => setText(e.target.value)} placeholder={t('desk_val_comment_ph')}
            className="flex-1 rounded-lg border border-border bg-background px-2 py-0.5 text-xs" />
          <button type="button" disabled={busy || !text.trim()}
            onClick={async () => {
              const ok = await onAnswer({ kind: 'comment', feature_key: topic, note: text.trim() })
              if (ok !== false) { setText(''); setOpen(false) }
            }}
            className="rounded-lg border border-border px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-50">{t('desk_val_save')}</button>
        </div>
      )}
      {open && suggestion && (
        <button type="button" data-latin-suggestion onClick={() => setText(suggestion)}
          className="mt-1 text-[11px] text-primary hover:underline text-left">
          {t('desk_val_latin_to_georgian')}: „{suggestion}"
        </button>
      )}
    </div>
  )
}

// One question, answered while the agent is on the phone. `cannot_tell` writes NO evidence — it
// records that the question was asked (chip_question_queue_v1's rule since 2026-09-08).
function Question({ q, busy, onAnswer }) {
  const [num, setNum] = useState('')
  // A year the save refused, shown instead of a Save button that silently did nothing (2026-09-16).
  const [yearErr, setYearErr] = useState(false)
  const saveYear = attribute => {
    if (num === '' || !yearBuiltValid(num)) { setYearErr(true); return }
    setYearErr(false)
    onAnswer({ kind: 'axis', attribute, value: Number(num) })
  }
  const Btn = ({ children, onClick, tone = '' }) => (
    <button type="button" disabled={busy} onClick={onClick}
      className={`rounded-lg border border-border px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-50 ${tone}`}>{children}</button>
  )
  return (
    <li className="py-2">
      {q.answer_via !== 'address' && <div className="text-sm font-medium">{q.question_ka}</div>}
      {q.why_ka && q.answer_via !== 'address' && <div className="text-[11px] text-muted-foreground">{q.why_ka}</div>}
      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
        {q.answer_via === 'chip' && (<>
          <Btn onClick={() => onAnswer({ kind: 'chip', feature_key: q.feature_key, answer: true })}>{t('desk_val_yes')}</Btn>
          <Btn onClick={() => onAnswer({ kind: 'chip', feature_key: q.feature_key, answer: false })}>{t('desk_val_no')}</Btn>
          <Btn tone="text-muted-foreground" onClick={() => onAnswer({ kind: 'chip', feature_key: q.feature_key, answer: null })}>{t('desk_val_cannot_tell')}</Btn>
        </>)}
        {/* A closed vocabulary is offered as the SAME set of choices a human agent gets on the
            portal form (owner ruling 2026-09-10), never as free text. */}
        {q.answer_via === 'choice' && (q.options || []).map(o => (
          <Btn key={o.feature_key} onClick={() => onAnswer({ kind: 'chip', feature_key: o.feature_key, answer: true })}>{o.name_ka}</Btn>
        ))}
        {/* A unit value with a fixed vocabulary (condition, building class). There is no feature
            code behind these, so the answer is recorded as a comment for the reading lane plus the
            value itself — the engine reads them from the listing, and an agent's correction is a
            statement about the advert, not a new feature. */}
        {q.answer_via === 'enum' && (q.options || []).map(o => (
          <Btn key={o.value} onClick={() => onAnswer({ kind: 'comment', feature_key: q.attribute || q.feature_key, note: `${q.name_ka}: ${o.name_ka} (${o.value})` })}>{o.name_ka}</Btn>
        ))}
        {/* ONE question, TWO answer paths (owner 2026-09-12: „ან რომელი პროექტია ერთი კითხვა
            უნდა იყოს"). The series implies the era, so a project chip and a typed year answer the
            same question; each chip carries the era it implies. Whichever the agent has, they give. */}
        {q.answer_via === 'choice_or_year' && (<>
          {(q.options || []).map(o => (
            <Btn key={o.feature_key} onClick={() => onAnswer({ kind: 'chip', feature_key: o.feature_key, answer: true })}>{o.name_ka}</Btn>
          ))}
          <input type="number" inputMode="numeric" step="1" value={num} onChange={e => { setNum(e.target.value); setYearErr(false) }}
            className="w-24 rounded-lg border border-border bg-background px-2 py-0.5 text-xs" placeholder={t('desk_val_year')} />
          <Btn onClick={() => saveYear(q.attribute || 'year_built')}>
            {t('desk_val_save')}
          </Btn>
          {yearErr && <span className="basis-full text-[11px] text-amber-600 dark:text-amber-400" data-val-year-invalid>{tLog('desk_val_year_invalid', { min: YEAR_BUILT_MIN, max: yearBuiltMax() })}</span>}
        </>)}
        {q.answer_via === 'axis' && (<>
          <input type="number" inputMode="decimal" step="any" value={num} onChange={e => setNum(e.target.value)}
            className="w-28 rounded-lg border border-border bg-background px-2 py-0.5 text-xs" placeholder={q.name_ka} />
          <Btn onClick={() => {
            if (q.attribute === 'year_built') return saveYear('year_built')
            const v = Number(num); if (num !== '' && Number.isFinite(v)) onAnswer({ kind: 'axis', attribute: q.attribute, value: v })
          }}>{t('desk_val_save')}</Btn>
          {yearErr && <span className="basis-full text-[11px] text-amber-600 dark:text-amber-400" data-val-year-invalid>{tLog('desk_val_year_invalid', { min: YEAR_BUILT_MIN, max: yearBuiltMax() })}</span>}
        </>)}
        {(q.answer_via === 'engine' || q.answer_via === 'text') && (
          <span className="text-[11px] text-muted-foreground">{q.name_ka}</span>
        )}
      </div>
      {q.answer_via === 'address' && <AddressQuestion q={q} busy={busy} onAnswer={onAnswer} />}
      <CommentBox topic={q.feature_key} comments={q.comments} busy={busy} onAnswer={onAnswer} />
    </li>
  )
}

// Question → the listing-form field(s) that answer it. A question NOT in this map is not a field of the
// listing form and stays in the question list (engine disagreement checks, other portal tick-boxes).
// address is NOT here any more (addr0, 2026-10-09): routing it into the form hid AddressQuestion entirely — the
// agent saw only a "მისამართი" chip. It is asked directly, first, outside the form (see the ask section).
const FORM_COVERS = {
  proj: ['year_built', 'project'], ceiling: ['ceiling_height'],
  elevator: ['elevator'], heat: ['heat'], parking: ['parking'], reno: ['condition'], bclass: ['class'],
}

// ── Agent adjustment (core 1994, owner 2026-09-23) ──────────────────────────────────────────────────────────────
// Secondary light (მეორადი განათება — a bedroom with no exterior window of its own) is NOT in the statistical estimate:
// adverts show no measurable effect yet. The agent may record a SEPARATE manual adjustment here — percent or amount,
// the bedrooms affected, a note. It never changes the model's figure above; it is stored apart so agent judgement can
// later be compared with market evidence. Append-only: a correction is a new entry.
function AgentAdjustments({ propertyId, agentId }) {
  const [rows, setRows] = useState(null)
  const [f, setF] = useState({ percent: '', amount: '', currency: 'USD', beds: '', note: '' })
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)
  // Stale-response guard: a response is applied only if its propertyId is still the
  // current one — switching property quickly must never show the previous flat's rows.
  const currentPid = useRef(propertyId)
  currentPid.current = propertyId
  const load = () => {
    const pid = propertyId
    return getValuationAdjustments(pid)
      .then(d => { if (currentPid.current === pid) setRows(d?.adjustments || []) })
      .catch(() => { if (currentPid.current === pid) setRows([]) })
  }
  useEffect(() => { setRows(null); setMsg(null); load() }, [propertyId])
  const set = (k, v) => setF(x => ({ ...x, [k]: v }))
  async function save() {
    setBusy(true); setMsg(null)
    try {
      await addValuationAdjustment({ property_id: propertyId, agent_id: agentId, reason: 'secondary_light',
        percent: f.percent === '' ? null : Number(f.percent.replace(',', '.')),
        amount: f.amount === '' ? null : parseMoneyInput(f.amount), currency: f.currency,
        affected_bedrooms: f.beds === '' ? null : Number(f.beds), note: f.note })
      // cleared only after the save confirms (owner ruling 2026-09-13: never clear input before the write lands)
      setF({ percent: '', amount: '', currency: f.currency, beds: '', note: '' })
      setMsg(t('desk_val_adj_saved')); load()
    } catch (e) { setMsg(`${t('desk_val_adj_failed')} ${e?.message || ''}`) } finally { setBusy(false) }
  }
  const inp = 'rounded-lg border border-input bg-background px-2 py-1.5 text-sm'
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <label className="text-xs text-muted-foreground">{t('desk_val_adj_percent')}
          <input className={`${inp} mt-1 w-full`} inputMode="decimal" placeholder="-5" value={f.percent} onChange={e => set('percent', e.target.value)} /></label>
        <label className="text-xs text-muted-foreground">{t('desk_val_adj_amount')}
          <span className="mt-1 flex gap-1">
            <input className={`${inp} w-full`} inputMode="decimal" value={f.amount} onChange={e => set('amount', e.target.value)} />
            <select className={inp} value={f.currency} onChange={e => set('currency', e.target.value)}><option>USD</option><option>GEL</option></select>
          </span></label>
        <label className="text-xs text-muted-foreground">{t('desk_val_adj_beds')}
          <input className={`${inp} mt-1 w-full`} inputMode="numeric" value={f.beds} onChange={e => set('beds', e.target.value.replace(/[^0-9]/g, ''))} /></label>
        <label className="col-span-2 text-xs text-muted-foreground sm:col-span-4">{t('desk_val_adj_note')}
          <input className={`${inp} mt-1 w-full`} value={f.note} onChange={e => set('note', e.target.value)} /></label>
      </div>
      <button type="button" disabled={busy || (f.percent === '' && f.amount === '')} onClick={save}
        className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50">{t('desk_val_adj_save')}</button>
      {msg && <p className="text-xs text-muted-foreground">{msg}</p>}
      {rows && rows.length > 0 && (
        <ul className="divide-y divide-border/60 text-xs">
          {rows.map(r => (
            <li key={r.id} className="py-1.5">
              <span className="font-medium">{r.adjustment_percent != null ? `${r.adjustment_percent > 0 ? '+' : ''}${r.adjustment_percent}%` : `${fmt(r.adjustment_amount)} ${r.currency}`}</span>
              {r.affected_bedrooms ? ` · ${sub('desk_val_adj_beds_n', { n: r.affected_bedrooms })}` : ''}
              {r.note ? ` · ${r.note}` : ''}
              <span className="text-muted-foreground"> · {new Date(r.created_at).toLocaleString('ka-GE')}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── THE SIMPLE TAB (owner 2026-09-23: "horrible design… it must be simple") ─────────────────
// Eight stacked blocks became four: the ANSWER (estimated price vs the ask), what to ASK the owner,
// the SIMILAR flats to negotiate with, and everything else behind one DETAILS row. Nothing was
// removed — the breakdown, statistics table, traits and adjustments are all still one click away.
function PriceAnswer({ bd, stats, subject }) {
  const e = bd && !bd.error ? (bd.estimate || {}) : {}
  // The breakdown's estimate first; the statistics' normal price per m² as the fallback.
  const ppm = e.ppm ?? stats?.normal_ppm ?? null
  const price = e.price ?? (ppm && subject?.area_sqm ? ppm * subject.area_sqm : null)
  const ask = subject?.price_usd ?? null
  const pct = ask && price ? Math.round((ask / price - 1) * 100) : null
  const verdict = pct == null ? null
    : pct > 3 ? { cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300', text: sub('desk_val_dearer_by', { p: pct }) }
    : pct < -3 ? { cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300', text: sub('desk_val_cheaper_by', { p: -pct }) }
    : { cls: 'bg-muted text-muted-foreground', text: t('desk_val_in_line') }
  if (!price) return (
    <section className="rounded-xl border border-border px-4 py-3 text-sm text-muted-foreground" data-val-answer>
      {t('desk_val_bd_none')}
    </section>
  )
  return (
    <section className="rounded-xl border border-border px-4 py-3" data-val-answer>
      <p className="text-xs text-muted-foreground">{t('desk_val_est_title')}</p>
      <p className="text-2xl font-semibold tabular-nums leading-tight">${fmt(price)}</p>
      <p className="text-xs text-muted-foreground tabular-nums">
        ${fmt(ppm)}/მ²{e.low_price && e.high_price ? ` · $${fmt(e.low_price)} – $${fmt(e.high_price)}` : ''}
      </p>
      {ask ? (
        <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between gap-2">
          <span className="text-sm tabular-nums">{t('desk_val_bd_ask')}: ${fmt(ask)}</span>
          {verdict && <span className={`text-xs rounded-md px-2 py-0.5 ${verdict.cls}`}>{verdict.text}</span>}
        </div>
      ) : null}
    </section>
  )
}

// The most-alike flats, one line each: where, size, floor, price, cheaper/dearer, and the links the
// agent shows the owner (owner 2026-09-12: they negotiate with these examples).
function SimilarList({ rows }) {
  const [all, setAll] = useState(false)
  if (!rows?.length) return null
  const shown = all ? rows : rows.slice(0, 5)
  const tone = { cheaper: 'text-emerald-600 dark:text-emerald-400', dearer: 'text-amber-600 dark:text-amber-400' }
  return (
    <section className="rounded-xl border border-border px-4 py-3" data-val-similar>
      <p className="text-sm font-semibold mb-1">{t('desk_val_similar_title')}</p>
      <ul className="divide-y divide-border/50">
        {shown.map(r => (
          <li key={r.listing_id || r.cluster_key} className="py-2 flex items-center justify-between gap-3 text-xs">
            <span className="min-w-0 truncate">
              {r.street_ka || '—'}{r.house_number ? ` ${r.house_number}` : ''}
              <span className="text-muted-foreground">{` · ${r.area_sqm} მ² · ${r.floor ?? '?'}/${r.floors_total ?? '?'}`}</span>
            </span>
            <span className="shrink-0 flex items-center gap-2 tabular-nums">
              <span className="font-medium">${fmt(r.price_usd)}</span>
              <span className={tone[r.price_verdict] || 'text-muted-foreground'}>{t(`desk_val_${r.price_verdict}`)}</span>
              {r.listing_id && (
                <a href={listingDeepLink(r.listing_id)} target="_blank" rel="noreferrer" className="text-primary" title={t('desk_val_open_card')}>
                  <Icon name="layers" />
                </a>
              )}
              {r.source_url && (
                <a href={r.source_url} target="_blank" rel="noreferrer" className="text-primary" title={r.source || 'portal'}>
                  <Icon name="external-link" />
                </a>
              )}
            </span>
          </li>
        ))}
      </ul>
      {rows.length > 5 && (
        <button type="button" onClick={() => setAll(a => !a)} className="mt-1 text-xs text-primary hover:underline">
          {all ? t('desk_val_fill_hide') : sub('desk_val_show_all', { n: rows.length })}
        </button>
      )}
    </section>
  )
}

// THE MARKET ROW (statistics engine, 2026-09-23): four numbers about the same kind of property in the same
// district — flats with the same rooms. The engine decides whether a number may be shown (5+ for a median,
// 10+ for a range); below its bar it says so, and we say so too — never a 0 for "not enough data".
function MarketRow({ m }) {
  if (!m || m.failed || !m.market) return null
  const a = m.market
  if (a.state === 'no_deal' || a.state === 'no_district' || a.state === 'error') return null
  const v = a.value || {}
  const has = (a.state === 'ok' || a.state === 'low_confidence') && v.median_ppm != null
  const rooms = m.filters?.rooms
  // Which area the engine actually answered for: the neighbourhood, or its district when that was too thin.
  const scope = [a.geo_level === 'neighbourhood' ? t('desk_val_mkt_hood') : t('desk_val_mkt_district'),
    rooms ? (rooms >= 4 ? '4+ ' : `${rooms} `) + t('desk_val_mkt_rooms') : null].filter(Boolean).join(' · ')
  const tile = (label, value) => (
    <div className="rounded-lg bg-muted/50 px-2.5 py-1.5 min-w-0">
      <div className="text-[11px] text-muted-foreground truncate">{label}</div>
      <div className="text-sm font-semibold tabular-nums truncate">{value}</div>
    </div>
  )
  return (
    <section className="rounded-xl border border-border px-4 py-3" data-val-market>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold">{t('desk_val_mkt_title')}</span>
        <span className="text-[11px] text-muted-foreground">
          {scope}{a.n != null ? ` · ${sub('desk_val_mkt_n', { n: a.n })}` : ''}
        </span>
      </div>
      {has ? (
        <>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {tile(t('desk_val_mkt_median'), `$${fmt(v.median_ppm)}/მ²`)}
            {tile(t('desk_val_mkt_range'), v.p25 != null && v.p75 != null ? `$${fmtK(v.p25)}–${fmtK(v.p75)}` : '—')}
            {tile(t('desk_val_mkt_listed'), fmt(v.inventory))}
            {tile(t('desk_val_mkt_days'), v.days_on_market != null ? fmt(v.days_on_market) : '—')}
          </div>
          {a.state === 'low_confidence' && <p className="mt-1.5 text-[11px] text-amber-600 dark:text-amber-400">{t('desk_val_mkt_low')}</p>}
        </>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">{t('desk_val_mkt_none')}</p>
      )}
    </section>
  )
}

// House, land and commercial: the engine's own comparable-group estimate (stats_valuation_v1). Not
// backtested, so no accuracy is claimed; the caveats it returns (unvalidated prices, asking not sale) stay.
function EngineValuation({ v }) {
  if (v.status === 'unsupported_kind') return (
    <section className="rounded-xl border border-border px-4 py-3 text-sm text-muted-foreground" data-val-answer>{t('desk_val_unsupported_kind')}</section>
  )
  const e = v.estimate?.usd
  if (v.status !== 'ok' || !e?.typical) return (
    <section className="rounded-xl border border-border px-4 py-3 text-sm text-muted-foreground" data-val-answer>
      {v.status === 'context' ? t('desk_val_need_size') : t('desk_val_bd_none')}
    </section>
  )
  return (
    <section className="rounded-xl border border-border px-4 py-3" data-val-answer>
      <p className="text-xs text-muted-foreground">{t('desk_val_est_title')}</p>
      <p className="text-2xl font-semibold tabular-nums leading-tight">${fmt(e.typical)}</p>
      <p className="text-xs text-muted-foreground tabular-nums">
        {v.ppm_usd ? `$${fmt(v.ppm_usd)}/მ² · ` : ''}${fmt(e.low)} – ${fmt(e.high)}
        {v.comparables?.n ? ` · ${sub('desk_val_mkt_n', { n: v.comparables.n })}` : ''}
      </p>
      {v.caveats?.includes('prices_unvalidated_no_rule_for_kind') && (
        <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">{t('desk_val_unvalidated')}</p>
      )}
    </section>
  )
}

export function CardAnalysis({ propertyId, agentId = null }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [flash, setFlash] = useState(null)
  const [open, setOpen] = useState({ fill: false, details: false })
  // The statistics engine's answer (2026-09-23) — fetched beside the analysis, never blocking it.
  const [mkt, setMkt] = useState(null)
  useEffect(() => {
    let gone = false
    setData(null); setErr(null); setMkt(null)
    getCardAnalysis(propertyId).then(d => { if (!gone) setData(d) }).catch(e => { if (!gone) setErr(e) })
    getMarketStats(propertyId).then(m => { if (!gone) setMkt(m) }).catch(() => { if (!gone) setMkt({ failed: true }) })
    return () => { gone = true }
  }, [propertyId])
  // After the embedded form saves: fetch the analysis again WITHOUT blanking it first, so the form
  // does not unmount mid-save and the agent keeps their place.
  const refetch = () => { getCardAnalysis(propertyId).then(setData).catch(() => {}) }
  const toggle = k => setOpen(o => ({ ...o, [k]: !o[k] }))
  // The answer and the recomputation are ONE round trip: the server records it and returns the
  // fresh analysis (owner: "the agent answers those while on the call and after that the system
  // calculates average price for it"). No second fetch, no stale table between them.
  // ⚠️ RETURNS whether the save landed, and every caller that destroys the agent's input must
  // wait for it. On 2026-09-13 four card-answer calls failed with 502 while the owner was writing
  // a comment; the box cleared and closed on the CLICK rather than on the result, so the text was
  // gone and the only trace was a flash line under a control that had already collapsed. A
  // failed write must never look like a successful one.
  const answer = async body => {
    setBusy(true); setFlash(null)
    try {
      const r = await answerCardQuestion({ property_id: propertyId, agent_id: agentId, ...body })
      if (r?.analysis) setData(r.analysis)
      setFlash(t('desk_val_saved'))
      return true
    } catch (e) {
      setFlash(String(e?.message || e).slice(0, 160))
      return false
    } finally {
      setBusy(false)
    }
  }
  if (err) return <p className="text-sm text-destructive">{t('desk_val_error')}: {String(err.message || err).slice(0, 160)}</p>
  if (!data) return <p className="text-sm text-muted-foreground">{t('desk_val_loading')}</p>
  // No flat analysis. For a house, land or commercial property the ENGINE values it; for other kinds it
  // says the kind is unsupported — never "no active advert" (owner ruling 6, 2026-09-23).
  if (data.error) {
    if (!mkt) return <p className="text-sm text-muted-foreground">{t('desk_val_loading')}</p>
    // core 2211: a non-flat property now says WHY there is no flat analysis; if the engine's answer did not arrive,
    // still say the kind is unsupported rather than "no active advert" (that is only for a property with none)
    // core 3139 (owner 2026-10-09, on a non-flat's Property valuation tab: "need to keep questions here"): what to ask
    // the owner on the call — land designation / road / utilities, commercial use / street entrance, a house's plot —
    // only while nothing answers it, at most 3. Answered through the same card-answer paths as the flat questions.
    const npq = data.questions || []
    const askList = npq.length > 0 && (
      <section className="rounded-xl border border-border px-4 py-3" data-val-ask>
        <span className="text-sm font-semibold">{t('desk_val_ask_owner')}</span>
        <ul className="mt-1 divide-y divide-border/40">
          {npq.map((q, i) => <Question key={`${q.kind}:${q.feature_key}:${i}`} q={q} busy={busy} onAnswer={answer} />)}
        </ul>
        {flash && <p className="mt-2 text-xs text-muted-foreground">{flash}</p>}
      </section>
    )
    if (!mkt.valuation) return (
      <div className="space-y-2" data-card-analysis>
        <p className="text-sm text-muted-foreground">{t(data.error === 'valuation_unsupported_for_kind' ? 'desk_val_unsupported_kind' : 'desk_val_no_unit')}</p>
        {askList}
      </div>
    )
    return (
      <div className="space-y-2" data-card-analysis>
        <EngineValuation v={mkt.valuation} />
        <MarketRow m={mkt} />
        {askList}
      </div>
    )
  }
  const qs = data.questions || []
  // Which open questions the listing form answers, and the form field each one marks.
  const formQ = qs.filter(q => FORM_COVERS[q.feature_key])
  const addrQ = qs.filter(q => q.answer_via === 'address')
  const restQ = qs.filter(q => !FORM_COVERS[q.feature_key] && q.answer_via !== 'address')
  const highlight = new Set(formQ.flatMap(q => FORM_COVERS[q.feature_key]))
  const listingId = data.subject?.listing_id || null
  return (
    <div className="space-y-2" data-card-analysis>
      {/* 1 — THE ANSWER: what the flat is worth, and how the ask compares. */}
      <PriceAnswer bd={data.valuation_breakdown} stats={data.stats} subject={data.subject} />
      <MarketRow m={mkt} />
      {/* WHAT THE OWNER SAID (2026-09-16) — kept, directly under the answer: without it agents
          typed „გაიყიდა" / „არ თანამშრომლობს" as comments, which nothing reads as a status. */}
      <MarketReportPanel propertyId={data.subject?.property_id || propertyId} agentId={agentId}
        listingId={listingId} />
      {/* 2 — WHAT TO ASK: only the missing facts, as chips. The listing form and the remaining
          questions open on demand instead of sitting inside the tab. */}
      <section className="rounded-xl border border-border px-4 py-3" data-val-ask>
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold">{t('desk_val_ask_owner')}</span>
          <button type="button" onClick={() => toggle('fill')}
            className="text-xs rounded-md border border-border px-2.5 py-1 hover:bg-muted">
            {open.fill ? t('desk_val_fill_hide') : t('desk_val_fill')}
          </button>
        </div>
        {qs.length ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {qs.map((q, i) => (
              <span key={`${q.kind}:${q.feature_key}:${i}`} className="text-xs rounded-md bg-muted px-2 py-0.5">
                {q.name_ka || q.feature_key}
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">{t('desk_val_nothing_to_ask')}</p>
        )}
        {addrQ.length > 0 && (
          <ul className="mt-2 divide-y divide-border/40" data-val-addr>
            {addrQ.map((q, i) => <Question key={`addr:${i}`} q={q} busy={busy} onAnswer={answer} />)}
          </ul>
        )}
        {open.fill && (
          <div className="mt-3 pt-3 border-t border-border/60">
            {listingId && (
              <ManualPropertyModal key={`${data.subject?.property_id}:${listingId}`} embedded highlight={highlight}
                existing={{ propertyId: data.subject?.property_id || propertyId, listingId }}
                agentId={agentId} onClose={() => {}} onSaved={refetch} />
            )}
            {restQ.length > 0 && (
              <ul className="mt-3 divide-y divide-border/40 border-t border-border/40">
                {restQ.map((q, i) => <Question key={`${q.kind}:${q.feature_key}:${i}`} q={q} busy={busy} onAnswer={answer} />)}
              </ul>
            )}
            <CommentBox topic={null} comments={data.comments} busy={busy} onAnswer={answer}
              label={t('desk_val_comment_flat')} className="mt-2 pt-2 border-t border-border/40" />
          </div>
        )}
        {flash && <p className="mt-2 text-xs text-muted-foreground">{flash}</p>}
      </section>
      {/* 3 — THE SIMILAR FLATS the agent negotiates with. */}
      <SimilarList rows={data.alike} />
      {/* 4 — EVERYTHING ELSE, one click away. */}
      <Section title={t('desk_val_details')} open={open.details} onToggle={() => toggle('details')}>
        <div className="space-y-3">
          <Subject s={data.subject || {}} cov={data.coverage} />
          {data.valuation_breakdown && (
            <div>
              <p className="text-xs font-semibold mb-1">{t('desk_val_bd_title')}</p>
              <Breakdown bd={data.valuation_breakdown} subject={data.subject} />
            </div>
          )}
          <div>
            <p className="text-xs font-semibold mb-1">{t('desk_val_stats')}</p>
            <Stats st={data.stats || {}} />
          </div>
          <div>
            <p className="text-xs font-semibold mb-1">{t('desk_val_traits')}</p>
            <Traits tr={data.traits || {}} floorPct={20} />
          </div>
          <div>
            <p className="text-xs font-semibold mb-1">{t('desk_val_adj_title')}</p>
            <AgentAdjustments propertyId={data.subject?.property_id || propertyId} agentId={agentId} />
          </div>
          <p className="text-[10px] text-muted-foreground text-right">{sub('desk_val_rule', { v: data.rule_version || '' })}</p>
        </div>
      </Section>
    </div>
  )
}

export default CardAnalysis
